import { useInfiniteQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import type { Currency } from "../../entities/ledger/types";
import { StatementPanel } from "../../features/accounts/StatementPanel";
import { SpendingByCategory } from "../../features/categories/SpendingByCategory";
import { ActivityPanel } from "../../features/movement/ActivityPanel";
import { MovementComposer } from "../../features/movement/MovementComposer";
import { listTransactions } from "../../features/transactions/api";
import { useDashboard } from "../../features/workspace/useDashboard";
import { humanError } from "../../shared/api/client";
import { ErrorPanel, LoadingPanel } from "../../shared/ui/PageState";
import { SectionTabs } from "../../shared/ui/SectionTabs";
import { JournalFilters } from "./JournalFilters";
import { monthRange, type Period } from "./period";

export type JournalView = "entries" | "statement" | "categories" | "record";

interface JournalPageProps {
  view: JournalView;
  currency: Currency;
  onNotify: (message: string) => void;
}

const TABS = [
  { to: "/journal", label: "Entries", end: true },
  { to: "/journal/statement", label: "Statement" },
  { to: "/journal/categories", label: "Categories" },
  { to: "/journal/record", label: "Record" },
];

const TITLES: Record<JournalView, { title: string; lede: string }> = {
  entries: { title: "Journal", lede: "Every fact recorded in this book, newest first. Corrections never erase: the original stays and a reversal is added next to it." },
  statement: { title: "Statement", lede: "One account over a period, with the running balance after each movement." },
  categories: { title: "Categories", lede: "Where the money went this period, grouped by the category on each transaction." },
  record: { title: "Record", lede: "Add an expense, an income or a transfer between your own accounts." },
};

// The journal is four views over the same book and period, not one long page.
// The period lives in the URL so a view can be linked to and survives a tab
// change; each view renders exactly one working surface.
export function JournalPage({ view, currency, onNotify }: JournalPageProps) {
  const dashboard = useDashboard(currency);
  const [params, setParams] = useSearchParams();
  const fallback = monthRange(0);
  const period: Period = { from: params.get("from") ?? fallback.from, to: params.get("to") ?? fallback.to };
  const accountID = params.get("account") ?? "";
  const categoryID = params.get("category") ?? "";
  const bookID = dashboard.data?.ids.book ?? "";
  const from = `${period.from}T00:00:00.000Z`;
  const to = `${period.to}T23:59:59.999Z`;
  const valid = period.from <= period.to;

  function update(next: Partial<Period & { account: string; category: string }>) {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value === "" || value === undefined) merged.delete(key);
      else merged.set(key, value);
    }
    setParams(merged, { replace: true });
  }

  const journal = useInfiniteQuery({
    queryKey: ["transactions", bookID, from, to, categoryID],
    enabled: bookID !== "" && view === "entries" && valid,
    initialPageParam: "",
    queryFn: ({ pageParam }) => listTransactions(bookID, from, to, pageParam as string, categoryID),
    getNextPageParam: (last) => (last.nextCursor === "" ? undefined : last.nextCursor),
  });

  const data = dashboard.data;
  const financial = data?.accounts ?? [];
  const entries = journal.data?.pages.flatMap((page) => page.transactions) ?? [];
  const copy = TITLES[view];
  const failed = dashboard.isError || (!dashboard.isPending && !data);

  return (
    <section className="journal-page view" aria-labelledby="journal-title">
      <div className="page-heading">
        <div>
          <p className="eyebrow">{data?.book.name ?? "Journal"}</p>
          <h1 id="journal-title">{copy.title}</h1>
          <p className="lede">{copy.lede}</p>
        </div>
      </div>

      <SectionTabs tabs={TABS} />

      {view === "record" || !data ? null : (
        <JournalFilters
          period={period}
          onPeriod={(next) => update(next)}
          accounts={view === "entries" ? financial : undefined}
          accountID={accountID}
          onAccount={view === "entries" ? (id) => update({ account: id }) : undefined}
          categories={view === "entries" ? data.categories : undefined}
          categoryID={categoryID}
          onCategory={view === "entries" ? (id) => update({ category: id }) : undefined}
        />
      )}

      {dashboard.isPending ? <LoadingPanel message="Loading the journal…" /> : null}
      {failed ? <ErrorPanel error={dashboard.error} onRetry={() => dashboard.refetch()} what="the journal" /> : null}

      {data && view === "entries" ? (
        accountID === "" ? (
          <>
            {journal.isError ? <p className="form-error" role="alert">{humanError(journal.error)}</p> : null}
            <ActivityPanel bookID={bookID} entries={entries} accounts={data.accounts} onNotify={onNotify} categories={data.categories} />
            <div className="load-more">
              <span>
                {journal.isFetching && entries.length === 0
                  ? "Loading…"
                  : journal.hasNextPage
                    ? `${entries.length} loaded, there are older ones`
                    : `${entries.length} ${entries.length === 1 ? "transaction" : "transactions"} in this period, all shown`}
              </span>
              {journal.hasNextPage ? (
                <button className="secondary-button" type="button" disabled={journal.isFetchingNextPage} onClick={() => journal.fetchNextPage()}>
                  {journal.isFetchingNextPage ? "Loading…" : "Load older transactions"}
                </button>
              ) : null}
            </div>
          </>
        ) : (
          <StatementPanel bookID={bookID} accounts={data.accounts} accountID={accountID} from={from} to={to} onNotify={onNotify} />
        )
      ) : null}

      {data && view === "statement" ? <StatementPanel bookID={bookID} accounts={data.accounts} from={from} to={to} onNotify={onNotify} /> : null}

      {data && view === "categories" ? <SpendingByCategory report={data.report} period={period} /> : null}

      {data && view === "record" ? (
        <div className="single-column">
          <MovementComposer ids={data.ids} currency={data.ids.baseCurrency} accounts={data.accounts} onNotify={onNotify} categories={data.categories} />
        </div>
      ) : null}
    </section>
  );
}
